/**
 * Chat state store — UI state, typing indicators, unread counts.
 *
 * React Query is the source of truth for conversations & messages.
 * This store only tracks: connection state, active conversation, typing, unread.
 * WS messages are pushed into the React Query cache by useWebSocket.
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";

// Re-export types for consumers
export interface ConversationResponse {
  id: string;
  type: "dm" | "group" | "broadcast";
  name: string | null;
  description: string | null;
  targetRole: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastMessage: {
    content: string;
    sender: string;
    createdAt: string;
  } | null;
  unreadCount: number;
  participants?: ParticipantInfo[];
}

export interface ParticipantInfo {
  userId: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
}

export interface MessageResponse {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderUsername: string;
  content: string;
  type: "text" | "system";
  createdAt: string;
}

export type WsServerMessage =
  | { type: "new_message"; message: MessageResponse }
  | { type: "typing"; conversationId: string; userId: string; username: string }
  | { type: "conversation_updated"; conversation: ConversationResponse }
  | { type: "unread_count"; conversationId: string; count: number }
  | { type: "error"; message: string }
  | { type: "order.assigned"; orderId: string; orderNumber: string }
  | { type: "order.rejected"; orderId: string; orderNumber: string }
  | { type: "order.hours_submitted"; orderId: string; orderNumber: string }
  | { type: "order.approved"; orderId: string; orderNumber: string }
  | { type: "payroll.approved"; payrollId: string; periodStart: string; periodEnd: string; amount: number }
  | { type: "pu_project.assigned"; projectId: number; projectName: string }
  | { type: "absence.updated"; absenceId: string; startDate: string; endDate: string }
  | { type: "absence.approved"; absenceId: string; startDate: string; endDate: string }
  | { type: "absence.rejected"; absenceId: string; startDate: string; endDate: string }
  | {
      type: "attendance.part_day_absence";
      absenceId: string;
      workerName: string;
      reason: string;
      date: string;
      startTime: string | null;
      endTime: string | null;
    }
  | { type: "data.changed"; topics: string[]; originClientId?: string };

// Typing indicator timeout
const TYPING_TIMEOUT_MS = 3000;
const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface ChatState {
  connected: boolean;
  activeConversationId: string | null;
  typingUsers: Record<string, { userId: string; username: string }[]>;
  totalUnread: number;

  setConnected: (connected: boolean) => void;
  setActiveConversation: (id: string | null) => void;
  setTotalUnread: (count: number) => void;
  addTypingUser: (conversationId: string, userId: string, username: string) => void;
  clearChat: () => void;
}

export const useChatStore = create<ChatState>()(
  devtools(
    (set) => ({
      connected: false,
      activeConversationId: null,
      typingUsers: {},
      totalUnread: 0,

      setConnected: (connected) => set({ connected }),

      setActiveConversation: (id) => set({ activeConversationId: id }),

      setTotalUnread: (count) => set({ totalUnread: count }),

      addTypingUser: (conversationId, userId, username) => {
        const key = `${conversationId}:${userId}`;
        const existing = typingTimers.get(key);
        if (existing) clearTimeout(existing);

        set((s) => {
          const current = s.typingUsers[conversationId] ?? [];
          const already = current.some((u) => u.userId === userId);
          return {
            typingUsers: {
              ...s.typingUsers,
              [conversationId]: already
                ? current
                : [...current, { userId, username }],
            },
          };
        });

        typingTimers.set(
          key,
          setTimeout(() => {
            set((s) => ({
              typingUsers: {
                ...s.typingUsers,
                [conversationId]: (s.typingUsers[conversationId] ?? []).filter(
                  (u) => u.userId !== userId,
                ),
              },
            }));
            typingTimers.delete(key);
          }, TYPING_TIMEOUT_MS),
        );
      },

      clearChat: () => {
        for (const timer of typingTimers.values()) clearTimeout(timer);
        typingTimers.clear();
        set({
          activeConversationId: null,
          typingUsers: {},
          totalUnread: 0,
          connected: false,
        });
      },
    }),
    { name: "ChatStore" },
  ),
);
